import { useTranslation } from 'react-i18next'

import { SubTitle, Text } from '@/components/common/UI'
import {
    Link,
    Modal,
    ModalBody,
    ModalContent,
    ModalHeader
} from '@/components/nextui'
import { AllDataAcademicOfferings } from '@/types/inscription'
import { Discount } from '@/types'

interface ModalPaymentProps {
    isOpen: boolean
    onClose: () => void
    text: string
    allDataAcademicOfferings: AllDataAcademicOfferings | null
    error: boolean
    discount: Discount | null
}

const validateModuloOrCicle = (cycle: string): boolean => {
    if (cycle.includes('MODULO') || cycle.includes('CICLO')) {
        return true
    }

    return false
}

const validateDiscount = (discount: Discount | null): boolean => {
    if (discount?.condiciones === 'AUTOMÁTICO') {
        return true
    }

    return false
}

const renderInformation = (cycle: string, discount: Discount) => {
    if (validateModuloOrCicle(cycle)) {
        return (
            <div className="space-y-2">
                <div>
                    <Text
                        text="1️⃣ Inscripción de unidades de estudio"
                        className="font-semibold"
                    />
                    <Text text="La Unidad Académica Administrativa será la encargada de inscribirte las unidades de estudio correspondientes, de acuerdo con tu tipo de ingreso." />
                </div>
                <div>
                    <Text
                        text="2️⃣ Aplicación de descuentos"
                        className="font-semibold "
                    />
                    <Text text="El área de Recursos Financieros realizará la postulación y aplicación del descuento que hayas solicitado." />
                    <p>
                        Si aún no has solicitado un descuento, recuerda que
                        puedes radicar un caso en la
                        <Link
                            isExternal
                            showAnchorIcon
                            href="https://universidadean.edu.co/mesa-de-servicios"
                            className="text-primary ml-1"
                        >
                            Mesa de Servicio
                        </Link>
                        , adjuntando los soportes necesarios.
                    </p>
                </div>
                <div>
                    <Text
                        text="3️⃣ Consulta de tu orden de matrícula"
                        className="font-semibold "
                    />
                    <Text text="Una vez finalizado el proceso de inscripción de unidades de estudio, podrás ingresar al sistema y consultar tu orden de matrícula." />
                </div>
            </div>
        )
    }

    if (!validateDiscount(discount)) {
        return (
            <div className="space-y-2">
                <div>
                    <Text
                        text="1️⃣ Aplicación de descuentos"
                        className="font-semibold "
                    />
                    <Text text="Desde el área de Recursos Financieros se realizará la postulación y aplicación del descuento que hayas solicitado." />
                    <p>
                        Si aún no has solicitado algún descuento, recuerda que
                        puedes radicar un caso en la
                        <Link
                            isExternal
                            showAnchorIcon
                            href="https://universidadean.edu.co/mesa-de-servicios"
                            className="text-primary ml-1"
                        >
                            Mesa de Servicio
                        </Link>
                        , adjuntando los soportes necesarios.
                    </p>
                </div>
                <div>
                    <Text
                        text="2️⃣ Consulta de tu orden de matrícula"
                        className="font-semibold "
                    />
                    <Text text="Una vez finalizado el proceso de postulación y aplicación de descuentos, podrás ingresar al sistema y consultar tu orden de matrícula.   <br/> " />
                </div>
            </div>
        )
    }
}

export const ModalPayment = ({
    isOpen,
    onClose,
    text,
    allDataAcademicOfferings,
    error,
    discount
}: ModalPaymentProps) => {
    const { t } = useTranslation()

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            backdrop="opaque"
            placement="center"
            size="5xl"
            motionProps={{
                variants: {
                    enter: {
                        y: 0,
                        opacity: 1,
                        transition: {
                            duration: 0.3,
                            ease: 'easeOut'
                        }
                    },
                    exit: {
                        y: -20,
                        opacity: 0,
                        transition: {
                            duration: 0.2,
                            ease: 'easeIn'
                        }
                    }
                }
            }}
        >
            <ModalContent>
                <ModalHeader>
                    <SubTitle
                        subTitle={t(`${text}.title`)}
                        className="text-primary"
                    />
                </ModalHeader>
                <ModalBody>
                    {error && allDataAcademicOfferings && discount ? (
                        <div className="text-justify max-h-[500px] overflow-y-auto">
                            <Text text={t(`${text}.description`)} />
                            <Text
                                text={allDataAcademicOfferings.program}
                                className="font-bold text-primary"
                            />
                            <Text
                                text={
                                    validateModuloOrCicle(
                                        allDataAcademicOfferings.cycle
                                    )
                                        ? 'Cuyo inicio se realizará en una fecha diferente a los periodos habituales de enero y julio'
                                        : ''
                                }
                            />
                            <Text
                                text="A continuación, te contamos lo que viene:"
                                className="mb-4"
                            />
                            {renderInformation(
                                allDataAcademicOfferings.cycle,
                                discount
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2 text-justify max-h-[500px] overflow-y-auto">
                            <div>
                                <Text text={t(`${text}.description`)} />
                                <Text
                                    text="¿Qué sigue después del pago de la matrícula?"
                                    className="font-bold"
                                />
                            </div>

                            <div>
                                <Text
                                    text="1️⃣ Envío de credenciales de acceso"
                                    className="font-semibold"
                                />
                                <Text text="Una vez confirmado tu pago, el área de TIC enviará a tu correo electrónico las credenciales con las que podrás ingresar a nuestras plataformas institucionales:" />
                                <ul className="list-disc pl-5 mt-2 space-y-1">
                                    <li>
                                        <Link
                                            isExternal
                                            showAnchorIcon
                                            href="https://outlook.office365.com/owa/universidadean.edu.co"
                                            className="text-primary"
                                        >
                                            Correo institucional (Office 365)
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            isExternal
                                            showAnchorIcon
                                            href="https://ean.instructure.com/"
                                            className="text-primary"
                                        >
                                            Canvas
                                        </Link>
                                    </li>
                                    <li>
                                        <Link
                                            isExternal
                                            showAnchorIcon
                                            href="https://universidadean.edu.co/estudiantes/mundo-sap-en-la-universidad-ean"
                                            className="text-primary"
                                        >
                                            SAP
                                        </Link>
                                    </li>
                                </ul>
                            </div>

                            <div>
                                <Text
                                    text="2️⃣ Inscripción de unidades de estudio"
                                    className="font-semibold"
                                />
                                <Text
                                    text="La Unidad Académica Administrativa se encargará de inscribirte las unidades de estudio correspondientes, de acuerdo con tu tipo de ingreso.
                                En los próximos días también recibirás información sobre tu jornada de bienvenida, un espacio en el que conocerás más sobre tu programa, tus docentes y la vida universitaria.
                                Puedes consultar los servicios a los que tendrás acceso como estudiante en el siguiente enlace:"
                                />

                                <Link
                                    isExternal
                                    showAnchorIcon
                                    href="https://universidadean.edu.co/estudiantes"
                                    className="text-primary"
                                >
                                    Estudiantes | Universidad Ean
                                </Link>
                            </div>

                            <div>
                                <Text
                                    text="¡Ya estás cada vez más cerca de iniciar esta nueva etapa académica!"
                                    className="font-semibold"
                                />

                                <Text text="Si necesitas apoyo durante el proceso, estaremos atentos para ayudarte." />
                            </div>
                        </div>
                    )}
                </ModalBody>
            </ModalContent>
        </Modal>
    )
}
